import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, SmartDataTable, getStatusBadge } from '@/backoffice/components/ui';
import { formatRelativeTime } from '@/backoffice/utils';
import { useAdminContext } from '@/backoffice/hooks/useAdminContext';
import DocumentViewer from '@/backoffice/components/admin/DocumentViewer';
import {
  fetchAllDocuments,
  validateDocument,
  downloadDocumentBlob,
  getPrimaryTypeName,
  type AdminDocument,
} from '@/domains/admin/services/admin.service';
import { FileText, Check, X, RefreshCw, Eye } from 'lucide-react';

export function AdminDocumentsPage() {
  const userType = useAdminContext();
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | undefined>(undefined);
  const [viewerTitle, setViewerTitle] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDocuments(await fetchAllDocuments());
    } catch {
      setError('Impossible de charger les documents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleValidate = async (doc: AdminDocument, status: 'valid' | 'dismiss' | 'suspended') => {
    try {
      await validateDocument(doc.id, status);
      await loadDocuments();
    } catch {
      setError('Échec de la validation du document.');
    }
  };

  const handleViewDocument = async (doc: AdminDocument) => {
    setActionLoading(true);
    try {
      const { blob } = await downloadDocumentBlob(doc.id);
      const url = window.URL.createObjectURL(blob);
      setViewerUrl(url);
      setViewerTitle(`${doc.category?.name || 'Document'} - ${doc.user?.firstnames} ${doc.user?.lastname}`);
      setViewerOpen(true);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Impossible d\'ouvrir le document');
    } finally {
      setActionLoading(false);
    }
  };

  const pending = documents.filter((d) => d.status === 'pending');
  const valid = documents.filter((d) => d.status === 'valid');
  const rejected = documents.filter((d) => d.status === 'dismiss' || d.status === 'suspended');

  return (
    <DashboardLayout userType={userType} title="Documents">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-amber-50 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-amber-700">En attente</p>
              <p className="text-2xl font-bold text-amber-900">{pending.length}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-emerald-700">Validés</p>
              <p className="text-2xl font-bold text-emerald-900">{valid.length}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-red-700">Rejetés / suspendus</p>
              <p className="text-2xl font-bold text-red-900">{rejected.length}</p>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        {loading ? (
          <p className="p-8 text-center text-gray-500">Chargement...</p>
        ) : (
          <SmartDataTable
            data={documents}
            columns={[
              {
                key: 'user',
                title: 'Utilisateur',
                render: (item) => (
                  <div>
                    <p className="font-medium text-gray-900">
                      {item.user
                        ? `${item.user.firstnames} ${item.user.lastname}`
                        : `User #${item.user_id}`}
                    </p>
                    {item.user && (
                      <p className="text-xs text-gray-500 capitalize">
                        {getPrimaryTypeName(item.user).replace('_', ' ')}
                      </p>
                    )}
                  </div>
                ),
              },
              {
                key: 'category',
                title: 'Type de document',
                render: (item) => (
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span>{item.category?.name ?? `Catégorie #${item.category_id}`}</span>
                  </div>
                ),
              },
              {
                key: 'status',
                title: 'Statut',
                render: (item) => getStatusBadge(item.status),
              },
              {
                key: 'created_at',
                title: 'Date',
                render: (item) => (item.created_at ? formatRelativeTime(item.created_at) : '—'),
              },
              {
                key: 'actions',
                title: 'Actions',
                render: (item) => (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Eye className="w-4 h-4" />}
                      isLoading={actionLoading}
                      onClick={() => handleViewDocument(item)}
                    >
                      Voir
                    </Button>
                    {item.status === 'pending' && (
                      <>
                        <Button size="sm" variant="success" onClick={() => handleValidate(item, 'valid')}>
                          Valider
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => handleValidate(item, 'dismiss')}>
                          Rejeter
                        </Button>
                      </>
                    )}
                  </div>
                ),
              },
            ]}
            keyExtractor={(item) => String(item.id)}
            emptyMessage="Aucun document"
          />
        )}
      </Card>

      <DocumentViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        url={viewerUrl}
        title={viewerTitle}
      />
    </DashboardLayout>
  );
}
